import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export type TenantRow = { id: string; slug: string; name: string };

type Ctx = {
  tenants: TenantRow[];
  activeTenantId: string | null;
  activeTenant: TenantRow | null;
  overrideActive: boolean;
  setActiveTenant: (id: string | null) => void;
  loading: boolean;
};

const TenantOverrideCtx = createContext<Ctx>({
  tenants: [], activeTenantId: null, activeTenant: null, overrideActive: false,
  setActiveTenant: () => {}, loading: false,
});

const STORAGE_KEY = "tricampus.superadmin.tenantOverride";

export function TenantOverrideProvider({ children }: { children: ReactNode }) {
  const { isSuperadmin, tenantId } = useAuth();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [override, setOverride] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSuperadmin) { setOverride(null); return; }
    try { setOverride(localStorage.getItem(STORAGE_KEY)); } catch { /* noop */ }
    setLoading(true);
    supabase.from("tenants").select("id,slug,name").order("name").then(({ data }) => {
      setTenants((data as TenantRow[] | null) ?? []);
      setLoading(false);
    });
  }, [isSuperadmin]);

  const setActiveTenant = useCallback((id: string | null) => {
    setOverride(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* noop */ }
  }, []);

  const value = useMemo<Ctx>(() => {
    const activeTenantId = isSuperadmin ? (override ?? tenantId ?? null) : (tenantId ?? null);
    const activeTenant = tenants.find(t => t.id === activeTenantId) ?? null;
    return {
      tenants,
      activeTenantId,
      activeTenant,
      overrideActive: isSuperadmin && !!override && override !== tenantId,
      setActiveTenant,
      loading,
    };
  }, [isSuperadmin, override, tenantId, tenants, setActiveTenant, loading]);

  return <TenantOverrideCtx.Provider value={value}>{children}</TenantOverrideCtx.Provider>;
}

export const useTenantOverride = () => useContext(TenantOverrideCtx);
